import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { SpecSection, SpecSubsection, SpecVersion } from '../types'
import Badge from '../components/Badge'
import { consumeNavTarget, flashHighlight } from '../lib/nav-target'

export default function SpecBookTab() {
  const [version, setVersion] = useState<SpecVersion | null>(null)
  const [sections, setSections] = useState<SpecSection[]>([])
  const [subs, setSubs] = useState<SpecSubsection[]>([])
  const [filter, setFilter] = useState('')
  const [activeSection, setActiveSection] = useState<string | null>(null)

  useEffect(() => {
    api.specBook.get().then((d) => {
      setVersion(d.version)
      setSections(d.sections)
      setSubs(d.subsections)
      if (d.sections.length > 0) setActiveSection(d.sections[0].id)
    })
  }, [])

  // Apply cross-tab nav target if one is queued for us. Run after subs load
  // so the target element exists in the DOM.
  useEffect(() => {
    if (subs.length === 0) return
    const t = consumeNavTarget('spec')
    if (!t?.specSubsectionId) return
    const target = subs.find((s) => s.id === t.specSubsectionId)
    if (target) setActiveSection(target.section_id)
    // Defer scroll to next paint so React renders the row first
    requestAnimationFrame(() => {
      const el = document.getElementById(`subsection-${t.specSubsectionId}`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      flashHighlight(el)
    })
  }, [subs])

  const filteredSubs = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return subs
    return subs.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.body.toLowerCase().includes(q) ||
        (s.brand_model ?? '').toLowerCase().includes(q)
    )
  }, [subs, filter])

  const subsBySection = useMemo(() => {
    const m = new Map<string, SpecSubsection[]>()
    for (const s of filteredSubs) {
      const arr = m.get(s.section_id) ?? []
      arr.push(s)
      m.set(s.section_id, arr)
    }
    return m
  }, [filteredSubs])

  const flagged = useMemo(() => subs.filter((s) => s.flag), [subs])

  if (!version) {
    return (
      <section>
        <h2>Heritage Spec Book</h2>
        <p className="muted">No active Spec Book version found.</p>
      </section>
    )
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Heritage Spec Book</h2>
          <p className="muted small">
            {version.version_label} · {sections.length} sections · {subs.length} specs
            {flagged.length > 0 && (
              <>
                {' '}· <span className="danger">{flagged.length} pending update</span>
              </>
            )}
          </p>
        </div>
        <input
          className="search"
          placeholder="Search Spec Book…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      {flagged.length > 0 && !filter && (
        <div className="callout mb-3" style={{ borderLeftColor: 'var(--danger)' }}>
          <strong>{flagged.length} mandatory updates queued from Strategy Ledger §1.3:</strong>
          <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
            {flagged.map((s) => (
              <li key={s.id}>
                <strong>{s.title}</strong> ({sectionTitle(sections, s.section_id)}) — {s.flag_note}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="spec-layout">
        <nav className="spec-nav">
          {sections.map((s) => {
            const items = subsBySection.get(s.id) ?? []
            if (filter && items.length === 0) return null
            return (
              <button
                key={s.id}
                className={`spec-nav-item ${activeSection === s.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveSection(s.id)
                  document
                    .getElementById(`section-${s.section_number}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
              >
                <span className="spec-nav-num">{s.section_number}</span>
                <span>{s.title}</span>
              </button>
            )
          })}
        </nav>

        <div className="spec-content">
          {sections.map((s) => {
            const items = subsBySection.get(s.id) ?? []
            if (filter && items.length === 0) return null
            return (
              <article key={s.id} id={`section-${s.section_number}`} className="spec-section">
                <h3>
                  {s.section_number}. {s.title}
                </h3>
                {items.map((item) => (
                  <div key={item.id} id={`subsection-${item.id}`} className="spec-item">
                    <div className="row-between mb-1">
                      <strong>{item.title}</strong>
                      <div className="row gap-2">
                        {item.brand_model && (
                          <Badge color="gold">{item.brand_model}</Badge>
                        )}
                        {item.flag === 'pending_update' && (
                          <Badge color="red">PENDING UPDATE</Badge>
                        )}
                      </div>
                    </div>
                    <pre className="spec-body">{item.body}</pre>
                    {item.flag_note && (
                      <div className="callout small mt-2" style={{ borderLeftColor: 'var(--danger)' }}>
                        {item.flag_note}
                      </div>
                    )}
                  </div>
                ))}
              </article>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function sectionTitle(sections: SpecSection[], id: string): string {
  return sections.find((s) => s.id === id)?.title ?? ''
}
